import { Color, Flex, Icon, Link, Spacing, Text } from '@billy/design-system'
import { useCallback } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { EmberRoute } from '../../../../../../../../enums/emberRoute'
import { useSegment } from '../../../../../../../../hooks'
import { useEmberRouteToUrlGenerator } from '../../../../../../../../hooks/routing/useEmberRouteToUrlGenerator'
import { useTransactionDrawerContext } from '../../contexts/TransactionDrawerContext'
import { BookingLine, BookingTotalLine } from '../BookingLine'
import { DrawerSection } from '../DrawerSection'
import * as Styled from './styles'

interface BookingsSectionProps {
  withTotal?: boolean
  originatorCurrency?: string | null
}

export const BookingsSection = ({ originatorCurrency, withTotal = true }: BookingsSectionProps) => {
  const { track } = useSegment()
  const {
    transactionId,
    originatorCurrencyId: transactionCurrency,
    postings,
    originatorType,
    transaction,
  } = useTransactionDrawerContext()
  const { t } = useTranslation()

  const { generateUrl } = useEmberRouteToUrlGenerator()

  const handleViewFullTableClick = useCallback(() => {
    if (generateUrl) {
      const transactionUrl = generateUrl(EmberRoute.Transaction, transactionId)
      window.open(transactionUrl, '_blank')
    }
    track('xxx - Bookkeeping - Ledger - Drawer', {
      action: 'View full table',
      transactionId,
      transactionType: originatorType,
    })
  }, [generateUrl, originatorType, track, transactionId])

  const isConvertedCurrency =
    !!originatorCurrency && !!transactionCurrency && originatorCurrency !== transactionCurrency

  const viewFullTableAction = (
    <Link underlined={false} colorVariant="highlight" onClick={handleViewFullTableClick}>
      <Flex gap={Spacing.S4} alignItems="center">
        <Text color={Color.Lavender500}>{t('ledger.view_full_table')}</Text>
        <Icon color={Color.Lavender500} icon="squareArrowOutUpRight" />
      </Flex>
    </Link>
  )

  const totalAmount = transaction?.amount || 0

  return (
    <DrawerSection title={t('ledger.bookings')} action={viewFullTableAction}>
      <Flex flexDirection="column">
        {postings?.map((posting) => (
          <BookingLine key={posting.id} posting={posting} />
        ))}
        {withTotal && <BookingTotalLine total={totalAmount} baseCurrency={transactionCurrency} />}
      </Flex>

      {withTotal && isConvertedCurrency && (
        <Flex gap={Spacing.S4} alignItems="flex-start">
          <Styled.IconLineWrapper>
            <Icon icon="info" />
          </Styled.IconLineWrapper>
          <Text colorVariant="discreet">
            <Trans
              i18nKey="ledger.converted_currency_info"
              values={{ baseCurrency: transactionCurrency, originatorCurrency }}
            />
          </Text>
        </Flex>
      )}
    </DrawerSection>
  )
}
